import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Calendar as CalIcon, MapPin, Clock, Filter, Trophy, Zap } from "lucide-react";

const CATEGORIES = ["Tous", "SENIORS", "U19", "U17", "U15", "U13", "LOISIRS"];

function fmt(d) {
  return new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [typeFilter, setTypeFilter] = useState("tous");
  const [statusFilter, setStatusFilter] = useState("upcoming"); // "upcoming" | "played" | "all"

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => events.filter((e) => {
    const cat = (e.category || "").toUpperCase();
    const catOk = filter === "Tous" || cat.startsWith(filter);
    const typOk = typeFilter === "tous" || e.type === typeFilter;
    const stOk = statusFilter === "all"
      || (statusFilter === "played" && e.status === "played")
      || (statusFilter === "upcoming" && e.status !== "played");
    return catOk && typOk && stOk;
  }), [events, filter, typeFilter, statusFilter]);

  // Sort by date (ascending for upcoming, descending for played)
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const r = (a.date || "").localeCompare(b.date || "");
      return statusFilter === "played" ? -r : r;
    });
    return arr;
  }, [filtered, statusFilter]);

  // Group by month
  const grouped = sorted.reduce((acc, e) => {
    const key = new Date(e.date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    (acc[key] = acc[key] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="calendar-page">
      <div className="mb-10">
        <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Agenda du club · Données officielles FFF</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-[#0F172A] tracking-tighter" style={{ fontFamily: "Outfit" }}>
          Calendrier<span className="text-[#DC2626]">.</span>
        </h1>
        <p className="mt-3 text-[#475569] max-w-2xl">
          Tous les matchs et entraînements du club, synchronisés avec la base officielle de la Fédération.
          Filtrez par catégorie, type ou résultat.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg">
        <div className="flex items-center gap-2 text-sm font-bold text-[#1E3A8A]">
          <Filter size={16} /> Filtrer :
        </div>
        <div className="flex flex-wrap gap-2 flex-1">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-cat-${c}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                filter === c ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#1E3A8A]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        <span className="text-xs font-bold text-[#475569] uppercase tracking-wider self-center">Statut :</span>
        {[
          { k: "upcoming", l: "À venir" },
          { k: "played", l: "Résultats" },
          { k: "all", l: "Tout" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setStatusFilter(t.k)}
            data-testid={`filter-status-${t.k}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              statusFilter === t.k ? "bg-[#D97706] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#D97706]"
            }`}
          >
            {t.l}
          </button>
        ))}
        <span className="w-px h-6 bg-[#E2E8F0] self-center mx-1" />
        <span className="text-xs font-bold text-[#475569] uppercase tracking-wider self-center">Type :</span>
        {[
          { k: "tous", l: "Tous" },
          { k: "match", l: "Matchs" },
          { k: "entrainement", l: "Entraînements" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTypeFilter(t.k)}
            data-testid={`filter-type-${t.k}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              typeFilter === t.k ? "bg-[#DC2626] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#DC2626]"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Events by month */}
      {Object.keys(grouped).length === 0 && (
        <div className="text-center py-16 border border-dashed border-[#E2E8F0] rounded-lg text-[#475569]">
          Aucun événement ne correspond à votre filtre.
        </div>
      )}

      {Object.entries(grouped).map(([month, items]) => (
        <div key={month} className="mb-10">
          <h2 className="text-xs tracking-[0.3em] uppercase font-bold text-[#D97706] mb-4 flex items-center gap-3">
            <span>{month}</span>
            <span className="h-px bg-[#E2E8F0] flex-1" />
            <span className="text-[#475569] normal-case tracking-normal text-[11px] font-normal">{items.length} événement{items.length > 1 ? "s" : ""}</span>
          </h2>
          <div className="grid gap-2">
            {items.map((e) => {
              const isPlayed = e.status === "played";
              const isHome = e.venue_type === "domicile";
              const vufcWon = isPlayed && parseInt(e.vufc_score || 0, 10) > parseInt(e.opp_score || 0, 10);
              const vufcLost = isPlayed && parseInt(e.vufc_score || 0, 10) < parseInt(e.opp_score || 0, 10);
              return (
                <div key={e.id} className="group border border-[#E2E8F0] rounded-lg p-4 bg-white hover:border-[#1E3A8A] transition-colors" data-testid={`event-${e.id}`}>
                  <div className="flex items-start gap-4">
                    <div className={`shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-md flex flex-col items-center justify-center text-white font-bold ${e.type === "match" ? "bg-[#DC2626]" : "bg-[#1E3A8A]"}`}>
                      <div className="text-[9px] uppercase tracking-wider opacity-80">{e.type === "match" ? "Match" : "Entraîn."}</div>
                      <div className="text-xl lg:text-2xl">{new Date(e.date).getDate()}</div>
                      <div className="text-[9px] uppercase opacity-80">{new Date(e.date).toLocaleDateString("fr-FR", { month: "short" })}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 bg-slate-100 rounded">{e.category}</span>
                        {e.source === "fff" && <span className="text-[10px] font-bold px-2 py-0.5 bg-[#D97706]/15 text-[#D97706] rounded">FFF</span>}
                        {isHome && !isPlayed && <span className="text-[10px] font-bold px-2 py-0.5 bg-[#1E3A8A]/10 text-[#1E3A8A] rounded">Domicile</span>}
                        {!isHome && !isPlayed && e.venue_type === "exterieur" && <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-200 text-slate-700 rounded">Extérieur</span>}
                        {isPlayed && vufcWon && <span className="text-[10px] font-bold px-2 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1"><Trophy size={10} /> Victoire</span>}
                        {isPlayed && vufcLost && <span className="text-[10px] font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded">Défaite</span>}
                        {isPlayed && !vufcWon && !vufcLost && <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-700 rounded">Nul</span>}
                      </div>
                      <h3 className="font-bold text-[#0F172A] leading-tight" style={{ fontFamily: "Outfit" }}>
                        {e.title}
                        {isPlayed && e.vufc_score !== "" && (
                          <span className="ml-2 text-[#DC2626]">· {isHome ? `${e.vufc_score} – ${e.opp_score}` : `${e.opp_score} – ${e.vufc_score}`}</span>
                        )}
                      </h3>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#475569]">
                        {e.time && <span className="flex items-center gap-1"><Clock size={12} /> {e.time}</span>}
                        <span className="flex items-center gap-1"><MapPin size={12} /> {e.location}</span>
                        <span className="flex items-center gap-1"><CalIcon size={12} /> {fmt(e.date)}</span>
                      </div>
                      {e.notes && <div className="mt-2 text-xs text-[#475569] italic">{e.notes}</div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
